<!--
// app/components/pages/teams-team/GridHistoricDataTeamPage.vue
-->
<script setup lang="ts">
import type { HistoricStandingData, Standing } from "~/src/standing/domain/models/Standing";
import type { TeamRankingParameter } from "~/src/team/domain/models/Team";
import { assignPositionOrdinalAbbreviation } from "~/src/team/domain/services/teamService";

type Props = {
  finalMatch: string | undefined;
  teamStanding: Standing | undefined;
  teamRankings: TeamRankingParameter[];
  showWorldCupStats?: boolean | undefined;
};

const props = defineProps<Props>();

const { locale } = useI18n();

const historicInfo = computed<HistoricStandingData>(() => {
  return {
    playoff: props.finalMatch,
    rank: props.teamStanding ? Number(props.teamStanding.rank) : undefined,
    points: props.teamStanding ? Number(props.teamStanding.points) : undefined,
    gameWon: props.teamStanding
      ? Number(props.teamStanding?.gameWon)
      : props.teamRankings.length
        ? props.teamRankings.find(r => r.parameter.code === "WIN")?.total
        : 0,
    gamePenaltyWon: props.teamStanding ? Number(props.teamStanding?.gamePenaltyWon) : 0,
    gamePenaltyLost: props.teamStanding ? Number(props.teamStanding?.gamePenaltyLost) : 0,
    gameLost: props.teamStanding
      ? Number(props.teamStanding?.gameLost)
      : props.teamRankings.length
        ? props.teamRankings.find(r => r.parameter.code === "LOSE")?.total
        : 0,
    goalPro: props.teamStanding
      ? Number(props.teamStanding?.goalPro)
      : props.teamRankings.length
        ? props.teamRankings.find(r => r.parameter.code === "GOL")?.total
        : 0,
    goalAgainst: props.teamStanding
      ? Number(props.teamStanding?.goalAgainst)
      : props.teamRankings.length
        ? props.teamRankings.find(r => r.parameter.code === "GOL-S")?.total
        : 0,
    goalDifference: props.teamStanding
      ? Number(props.teamStanding?.goalPro) - Number(props.teamStanding?.goalAgainst)
      : props.teamRankings &&
          props.teamRankings.find(r => r.parameter.code === "GOL") &&
          props.teamRankings.find(r => r.parameter.code === "GOL-S")
        ? Number(props.teamRankings.find(r => r.parameter.code === "GOL")?.total) -
          Number(props.teamRankings.find(r => r.parameter.code === "GOL-S")?.total)
        : 0,
    yellowCards: props.teamRankings.find(r => r.parameter.code === "CRT-G")?.total,
    redCards: props.teamRankings.find(r => r.parameter.code === "CRT-R")?.total,
  };
});
</script>

<template>
  <div class="container-historic-stats">
    <div class="box-historic-data" v-if="historicInfo.playoff">
      <!--      Playoff icon-->
      <div class="text-primary icon-playoff">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="playoff">
            <path
              id="Vector"
              d="M5.78906 8.64575C5.90899 10.2109 6.61539 11.6729 7.76695 12.7397C8.9185 13.8064 10.4304 14.399 12 14.399C13.5697 14.399 15.0815 13.8064 16.2331 12.7397C17.3846 11.6729 18.0911 10.2109 18.211 8.64575"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_2"
              d="M19.1875 1.93758C19.1875 2.19175 19.0865 2.4355 18.9068 2.61523C18.7271 2.79494 18.4833 2.89591 18.2292 2.89591H5.77083C5.51666 2.89591 5.27291 2.79494 5.09319 2.61523C4.91347 2.4355 4.8125 2.19175 4.8125 1.93758C4.8125 1.68341 4.91347 1.43966 5.09319 1.25994C5.27291 1.08022 5.51666 0.979248 5.77083 0.979248H18.2292C18.4833 0.979248 18.7271 1.08022 18.9068 1.25994C19.0865 1.43966 19.1875 1.68341 19.1875 1.93758Z"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_3"
              d="M19.1875 7.68758C19.1875 7.94175 19.0865 8.1855 18.9068 8.36523C18.7271 8.54494 18.4833 8.64591 18.2292 8.64591H5.77083C5.51666 8.64591 5.27291 8.54494 5.09319 8.36523C4.91347 8.1855 4.8125 7.94175 4.8125 7.68758C4.8125 7.43341 4.91347 7.18966 5.09319 7.00993C5.27291 6.83022 5.51666 6.72925 5.77083 6.72925H18.2292C18.4833 6.72925 18.7271 6.83022 18.9068 7.00993C19.0865 7.18966 19.1875 7.43341 19.1875 7.68758Z"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_4"
              d="M18.2291 6.72909V2.89575"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_5"
              d="M5.77087 2.89575V6.72909"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_6"
              d="M8.16671 5.05217C8.03439 5.05217 7.92712 4.9449 7.92712 4.81258C7.92712 4.68026 8.03439 4.573 8.16671 4.573"
              stroke="currentColor"
            />
            <path
              id="Vector_7"
              d="M8.16663 5.05217C8.29894 5.05217 8.40621 4.9449 8.40621 4.81258C8.40621 4.68026 8.29894 4.573 8.16663 4.573"
              stroke="currentColor"
            />
            <path
              id="Vector_8"
              d="M12 5.05217C11.8676 5.05217 11.7604 4.9449 11.7604 4.81258C11.7604 4.68026 11.8676 4.573 12 4.573"
              stroke="currentColor"
            />
            <path
              id="Vector_9"
              d="M12 5.05217C12.1323 5.05217 12.2396 4.9449 12.2396 4.81258C12.2396 4.68026 12.1323 4.573 12 4.573"
              stroke="currentColor"
            />
            <path
              id="Vector_10"
              d="M15.8333 5.05217C15.701 5.05217 15.5938 4.9449 15.5938 4.81258C15.5938 4.68026 15.701 4.573 15.8333 4.573"
              stroke="currentColor"
            />
            <path
              id="Vector_11"
              d="M15.8334 5.05217C15.9657 5.05217 16.073 4.9449 16.073 4.81258C16.073 4.68026 15.9657 4.573 15.8334 4.573"
              stroke="currentColor"
            />
            <path
              id="Vector_12"
              d="M13.4375 14.229V17.6004C13.4375 18.1042 13.5368 18.603 13.7296 19.0683C13.9226 19.5337 14.2053 19.9565 14.5617 20.3125L15.3542 21.1041H8.64587L9.43842 20.3125C9.7948 19.9565 10.0775 19.5337 10.2704 19.0683C10.4633 18.603 10.5625 18.1042 10.5625 17.6004V14.229"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_13"
              d="M15.3542 21.1042C15.6083 21.1042 15.8521 21.2053 16.0318 21.3849C16.2115 21.5646 16.3125 21.8084 16.3125 22.0626C16.3125 22.3167 16.2115 22.5605 16.0318 22.7402C15.8521 22.9199 15.6083 23.0209 15.3542 23.0209H8.64583C8.39166 23.0209 8.14791 22.9199 7.96819 22.7402C7.78847 22.5605 7.6875 22.3167 7.6875 22.0626C7.6875 21.8084 7.78847 21.5646 7.96819 21.3849C8.14791 21.2053 8.39166 21.1042 8.64583 21.1042"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_14"
              d="M9.60413 17.2708H14.3958"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </g>
        </svg>
      </div>
      <p class="title-historic-data">{{ showWorldCupStats ? "Round" : $t("team.playoff.standing.title") }}</p>
      <p class="value-historic-data">
        {{
          historicInfo.playoff === "winner"
            ? $t(`team.historicPlayoff.${historicInfo.playoff}`)
            : historicInfo.playoff
        }}
      </p>
    </div>
    <!--    Regular phase icon-->
    <div class="box-historic-data regular-phase" v-if="!showWorldCupStats">
      <div class="text-primary icon-regular-phase">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="fase_regular">
            <path
              id="Vector"
              d="M12.5644 5.39616L13.793 7.92712H16.1841C16.3052 7.92164 16.425 7.95382 16.527 8.01924C16.6291 8.08465 16.7084 8.18008 16.754 8.2924C16.7996 8.40472 16.8093 8.5284 16.7817 8.64645C16.7541 8.76449 16.6906 8.87109 16.6 8.95158L14.5242 11.1203L15.6742 13.7662C15.7251 13.8873 15.7366 14.0213 15.7072 14.1493C15.6778 14.2772 15.6089 14.3927 15.5103 14.4795C15.4117 14.5662 15.2884 14.6198 15.1577 14.6326C15.027 14.6455 14.8956 14.617 14.782 14.5511L12 12.9852L9.22082 14.5511C9.10721 14.617 8.9758 14.6455 8.84512 14.6326C8.71444 14.6198 8.5911 14.5662 8.49251 14.4795C8.39392 14.3927 8.32506 14.2772 8.29565 14.1493C8.26624 14.0213 8.27777 13.8873 8.32861 13.7662L9.47861 11.1203L7.39998 8.95158C7.30949 8.87119 7.24609 8.76477 7.21847 8.64693C7.19086 8.52908 7.20038 8.40558 7.24573 8.29336C7.29108 8.18114 7.37004 8.0857 7.47178 8.02012C7.57351 7.95455 7.69305 7.92207 7.81398 7.92712H10.2098L11.4413 5.39616C11.4951 5.2938 11.5758 5.20808 11.6748 5.14829C11.7738 5.08849 11.8872 5.05688 12.0029 5.05688C12.1185 5.05688 12.2319 5.08849 12.3309 5.14829C12.4299 5.20808 12.5106 5.2938 12.5644 5.39616Z"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_2"
              d="M17.7423 13.679L18.7294 11.5563C18.7723 11.4735 18.8372 11.4041 18.9168 11.3556C18.9965 11.3071 19.088 11.2815 19.1813 11.2815C19.2745 11.2815 19.366 11.3071 19.4457 11.3556C19.5254 11.4041 19.5902 11.4735 19.6331 11.5563L20.6173 13.679H22.534C22.6306 13.6752 22.7261 13.7013 22.8074 13.7537C22.8886 13.8062 22.9517 13.8824 22.988 13.9721C23.0243 14.0617 23.032 14.1604 23.0101 14.2546C22.9882 14.3488 22.9377 14.434 22.8656 14.4984L21.2 16.1362L22.1219 18.2531C22.1626 18.3501 22.1718 18.4574 22.1481 18.5598C22.1245 18.6623 22.0692 18.7547 21.9902 18.824C21.9111 18.8933 21.8123 18.936 21.7076 18.946C21.603 18.956 21.4978 18.9329 21.407 18.8799L19.1789 17.6274L16.9517 18.8799C16.8609 18.9329 16.7558 18.956 16.6511 18.946C16.5465 18.936 16.4476 18.8933 16.3686 18.824C16.2895 18.7547 16.2342 18.6623 16.2106 18.5598C16.1869 18.4574 16.1961 18.3501 16.2368 18.2531L16.7648 17.0399"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_3"
              d="M7.23905 17.0514L7.7623 18.2531C7.80298 18.3501 7.81214 18.4574 7.7885 18.5598C7.76485 18.6623 7.70959 18.7547 7.63054 18.824C7.55148 18.8933 7.45264 18.936 7.34798 18.946C7.24333 18.956 7.13817 18.9329 7.04738 18.8799L4.81926 17.6274L2.59209 18.8799C2.5013 18.9329 2.39614 18.956 2.29149 18.946C2.18684 18.936 2.08799 18.8933 2.00894 18.824C1.92988 18.7547 1.87462 18.6623 1.85097 18.5598C1.82733 18.4574 1.83649 18.3501 1.87717 18.2531L2.80005 16.1362L1.13542 14.4984C1.06297 14.4343 1.0121 14.3495 0.989803 14.2554C0.967503 14.1613 0.97486 14.0626 1.01087 13.9728C1.04687 13.8831 1.10976 13.8066 1.19091 13.754C1.27206 13.7014 1.3675 13.6752 1.46413 13.679H3.3808L4.36788 11.5563C4.41078 11.4735 4.47562 11.4041 4.5553 11.3556C4.63499 11.3071 4.72647 11.2815 4.81974 11.2815C4.91301 11.2815 5.00448 11.3071 5.08417 11.3556C5.16386 11.4041 5.22869 11.4735 5.27159 11.5563L6.2558 13.679"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </g>
        </svg>
      </div>
      <p class="title-historic-data">{{ $t("team.regularPhase.historic.title") }}</p>
      <p class="value-historic-data">
        {{ !!historicInfo.rank ? historicInfo.rank : "-"
        }}<span v-if="locale === 'de'">{{
          assignPositionOrdinalAbbreviation(historicInfo.rank, locale)
        }}</span
        ><sup v-else class="-top-4">{{ assignPositionOrdinalAbbreviation(historicInfo.rank, locale) }}</sup>
      </p>
    </div>
    <!--    Points icon-->
    <div class="box-historic-data points" v-if="!showWorldCupStats">
      <div class="text-primary icon-points">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="flag">
            <path
              id="Vector"
              d="M4.33337 1.9375H10.0834C10.3375 1.9375 10.5813 2.03847 10.761 2.21819C10.9407 2.39791 11.0417 2.64167 11.0417 2.89583V4.8125H20.625C21.1521 4.8125 21.247 5.08179 20.8349 5.41146L14.3959 10.5625L21.5834 16.3125H7.78337C7.70481 16.3126 7.62743 16.2934 7.55804 16.2566C7.48865 16.2197 7.42938 16.1664 7.38545 16.1013C7.34152 16.0361 7.31428 15.9612 7.30613 15.883C7.29798 15.8049 7.30917 15.726 7.33871 15.6532C8.00379 14.0068 11.0417 13.4375 11.0417 13.4375H4.33337"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_2"
              d="M11.0416 4.8125V13.4375"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_3"
              d="M2.41663 0.979248V23.0209"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </g>
        </svg>
      </div>
      <p class="title-historic-data">{{ $t("team.points.historic.title") }}</p>
      <p class="value-historic-data">{{ historicInfo.points || "-" }}</p>
    </div>
    <!--    Games won Icon-->
    <div class="box-historic-data games-won">
      <div class="text-primary icon-game-won">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="award-medal" clip-path="url(#clip0_3217_23894)">
            <path
              id="Vector"
              d="M4.8125 15.8333C4.8125 17.7395 5.56975 19.5677 6.91767 20.9156C8.26559 22.2635 10.0938 23.0208 12 23.0208C13.9062 23.0208 15.7344 22.2635 17.0823 20.9156C18.4302 19.5677 19.1875 17.7395 19.1875 15.8333C19.1875 13.927 18.4302 12.0988 17.0823 10.7509C15.7344 9.403 13.9062 8.64575 12 8.64575C10.0938 8.64575 8.26559 9.403 6.91767 10.7509C5.56975 12.0988 4.8125 13.927 4.8125 15.8333Z"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_2"
              d="M17.75 0.979248L15.0072 6.73883"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_3"
              d="M16.9814 7.65883L20.1459 0.979248"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_4"
              d="M20.1459 0.979248L16.9814 7.65883"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_5"
              d="M18.6968 8.98612L22.5416 0.979248"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_6"
              d="M6.25 0.979248L8.99275 6.73883"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_7"
              d="M7.01758 7.65883L3.85413 0.979248"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_8"
              d="M3.85413 0.979248L7.01758 7.65883"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_9"
              d="M5.30129 8.98612L1.45837 0.979248"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_10"
              d="M12.4524 12.275L13.4375 14.3958H15.3542C15.4508 14.3919 15.5463 14.418 15.6276 14.4704C15.7088 14.5229 15.7719 14.5992 15.8082 14.6888C15.8445 14.7785 15.8522 14.8771 15.8303 14.9713C15.8084 15.0655 15.7579 15.1507 15.6858 15.2151L14.0221 16.852L14.944 18.9689C14.9844 19.0659 14.9933 19.173 14.9696 19.2753C14.9459 19.3776 14.8907 19.4699 14.8117 19.5392C14.7328 19.6085 14.6342 19.6513 14.5297 19.6616C14.4251 19.6719 14.32 19.6492 14.2291 19.5966L12 18.346L9.77382 19.5985C9.6829 19.6511 9.57779 19.6738 9.47328 19.6635C9.36877 19.6532 9.27012 19.6104 9.19121 19.5411C9.11229 19.4718 9.05709 19.3796 9.03335 19.2773C9.00962 19.175 9.01856 19.0678 9.05891 18.9708L9.98083 16.8539L8.31716 15.217C8.24394 15.1532 8.1924 15.0681 8.16964 14.9737C8.14689 14.8792 8.15407 14.78 8.19017 14.6898C8.22627 14.5996 8.28952 14.5228 8.37116 14.4702C8.4528 14.4175 8.5488 14.3915 8.64587 14.3958H10.5625L11.5487 12.273C11.5915 12.19 11.6564 12.1204 11.7362 12.0719C11.816 12.0234 11.9077 11.9978 12.0011 11.998C12.0945 11.9982 12.1861 12.0242 12.2657 12.0731C12.3453 12.1219 12.4099 12.1918 12.4524 12.275Z"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </g>
          <defs>
            <clipPath id="clip0_3217_23894">
              <rect width="24" height="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </div>
      <p class="title-historic-data">{{ $t("team.wins.standing.title") }}</p>
      <p class="value-historic-data">{{ historicInfo.gameWon }}</p>
    </div>
    <!--    penalty won icon-->
    <div class="box-historic-data penalty-won" v-if="!showWorldCupStats">
      <div class="text-primary icon-penalty-won">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="soccer-goalkeeper-glove-1">
            <path
              id="Vector"
              d="M10.7437 19.0161L4.64485 12.9172L1.93756 15.6274C1.84846 15.7164 1.77777 15.8221 1.72954 15.9384C1.68132 16.0548 1.65649 16.1795 1.65649 16.3054C1.65649 16.4314 1.68132 16.5561 1.72954 16.6724C1.77777 16.7887 1.84846 16.8944 1.93756 16.9834L6.68131 21.7262C6.86102 21.9059 7.10474 22.0068 7.35885 22.0068C7.61297 22.0068 7.85668 21.9059 8.03639 21.7262L10.7437 19.0161Z"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_2"
              d="M10.7437 19.0159C12.0997 20.3709 14.3968 19.128 16.5042 18.6766C17.7638 18.403 19.0084 18.0646 20.2331 17.6627C21.2499 17.3244 22.0989 16.6775 22.0299 15.6205C21.9965 15.1133 21.7629 14.6402 21.3807 14.3052C20.9984 13.9702 20.4987 13.8008 19.9916 13.8342L16.8425 14.2721L21.9217 9.19295C22.1912 8.92328 22.3426 8.55758 22.3425 8.1763C22.3424 7.79502 22.1908 7.42939 21.9212 7.15984C21.6515 6.8903 21.2858 6.73892 20.9045 6.73901C20.5232 6.7391 20.1576 6.89065 19.8881 7.16032L16.5042 10.5452"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_3"
              d="M19.8918 7.15755L20.5693 6.47905C20.7066 6.34644 20.8161 6.18782 20.8914 6.01244C20.9668 5.83706 21.0064 5.64843 21.0081 5.45756C21.0098 5.26669 20.9734 5.0774 20.9011 4.90074C20.8288 4.72407 20.7221 4.56357 20.5871 4.4286C20.4521 4.29363 20.2916 4.18689 20.115 4.11461C19.9383 4.04234 19.749 4.00596 19.5582 4.00762C19.3673 4.00928 19.1787 4.04894 19.0033 4.12427C18.8279 4.19961 18.6693 4.30912 18.5367 4.44642L17.8591 5.12396"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_4"
              d="M14.4706 8.51253L17.8593 5.12386C17.993 4.9904 18.0991 4.8319 18.1716 4.65743C18.244 4.48295 18.2814 4.29591 18.2816 4.10699C18.2818 3.91806 18.2447 3.73095 18.1726 3.55634C18.1005 3.38173 17.9947 3.22303 17.8612 3.08932C17.7277 2.9556 17.5692 2.84948 17.3948 2.77702C17.2203 2.70456 17.0332 2.66717 16.8443 2.66699C16.4628 2.66663 16.0967 2.81786 15.8267 3.0874L12.438 6.47894"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_5"
              d="M14.4707 4.4464C14.6132 4.31507 14.7276 4.15628 14.8072 3.97957C14.8867 3.80287 14.9298 3.61191 14.9336 3.41816C14.9375 3.22441 14.9022 3.03188 14.8297 2.85214C14.7573 2.67239 14.6493 2.50915 14.5122 2.37222C14.375 2.23529 14.2116 2.1275 14.0318 2.05532C13.852 1.98315 13.6594 1.94808 13.4656 1.95223C13.2719 1.95638 13.081 1.99967 12.9044 2.07948C12.7278 2.15928 12.5692 2.27398 12.438 2.41665L6.33921 8.51261C4.619 10.2376 4.23663 11.4787 4.64488 12.9209"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </g>
        </svg>
      </div>
      <p class="title-historic-data">{{ $t("team.pWins.standing.title") }}</p>
      <p class="value-historic-data">{{ historicInfo.gamePenaltyWon ? historicInfo.gamePenaltyWon : 0 }}</p>
    </div>
    <!--    penalty lost icon-->
    <div class="box-historic-data penalty-lost" v-if="!showWorldCupStats">
      <div class="text-primary icon-penalty-lost">
        <svg width="19" height="24" viewBox="0 0 15 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="Group">
            <path
              id="Vector"
              d="M6.0957 2.95982C6.0957 3.4796 6.30218 3.97809 6.66972 4.34563C7.03726 4.71316 7.53575 4.91964 8.05553 4.91964C8.5753 4.91964 9.07379 4.71316 9.44133 4.34563C9.80887 3.97809 10.0153 3.4796 10.0153 2.95982C10.0153 2.44004 9.80887 1.94156 9.44133 1.57402C9.07379 1.20648 8.5753 1 8.05553 1C7.53575 1 7.03726 1.20648 6.66972 1.57402C6.30218 1.94156 6.0957 2.44004 6.0957 2.95982Z"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_2"
              d="M1 17.4624C1 17.8782 1.16518 18.277 1.45921 18.571C1.75324 18.8651 2.15204 19.0302 2.56786 19.0302C2.98368 19.0302 3.38247 18.8651 3.6765 18.571C3.97053 18.277 4.13571 17.8782 4.13571 17.4624C4.13571 17.0466 3.97053 16.6478 3.6765 16.3537C3.38247 16.0597 2.98368 15.8945 2.56786 15.8945C2.15204 15.8945 1.75324 16.0597 1.45921 16.3537C1.16518 16.6478 1 17.0466 1 17.4624Z"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_3"
              d="M6.72266 14.3896L7.24789 11.7007"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_4"
              d="M13.4331 3.39089C13.2838 3.3534 13.1285 3.34578 12.9763 3.36848C12.8241 3.39118 12.6778 3.44374 12.546 3.52315C12.4141 3.60255 12.2992 3.70724 12.208 3.83118C12.1167 3.95513 12.0508 4.09589 12.0141 4.24538L11.8182 5.02931C11.7086 5.4484 11.4627 5.81913 11.1192 6.08311C10.7757 6.3471 10.3542 6.48933 9.92105 6.48741H5.79759C4.83605 6.48723 3.90214 6.80905 3.14483 7.40154C2.38752 7.99403 1.85044 8.82306 1.61925 9.7564L1.4311 10.5403C1.39244 10.6901 1.38392 10.8461 1.40605 10.9993C1.42818 11.1524 1.48051 11.2996 1.56001 11.4324C1.63951 11.5651 1.74459 11.6807 1.86915 11.7725C1.99371 11.8643 2.13526 11.9304 2.28559 11.9671C2.43958 12.0078 2.60023 12.0171 2.75789 11.9943C2.91555 11.9715 3.06699 11.9171 3.20314 11.8344C3.33929 11.7517 3.45734 11.6424 3.55022 11.513C3.6431 11.3835 3.70891 11.2367 3.74369 11.0812L3.93184 10.2973C4.03886 9.86381 4.29069 9.47986 4.64566 9.209C5.00063 8.93814 5.43745 8.79661 5.88382 8.80784L4.79416 14.0131C4.76945 14.1398 4.76993 14.27 4.79556 14.3964C4.82119 14.5229 4.87147 14.643 4.94354 14.7501C5.0156 14.8571 5.10804 14.9488 5.21557 15.0201C5.3231 15.0914 5.44362 15.1408 5.57025 15.1655C5.69687 15.1902 5.82712 15.1897 5.95356 15.1641C6.08001 15.1385 6.20016 15.0882 6.30717 15.0161C6.41418 14.9441 6.50594 14.8516 6.57723 14.7441C6.64851 14.6366 6.69792 14.516 6.72262 14.3894L6.0406 17.8544C6.00824 17.9834 6.00248 18.1176 6.02367 18.2489C6.04485 18.3802 6.09255 18.5058 6.16384 18.6181C6.23514 18.7304 6.32854 18.827 6.43837 18.902C6.5482 18.977 6.67214 19.0289 6.80266 19.0545C6.93318 19.08 7.06754 19.0788 7.19755 19.0507C7.32757 19.0227 7.45051 18.9685 7.55891 18.8914C7.6673 18.8144 7.75887 18.716 7.82803 18.6024C7.8972 18.4888 7.94252 18.3623 7.96123 18.2307L9.8505 8.82352C10.8241 8.83964 11.7744 8.52513 12.5461 7.93138C13.3178 7.33763 13.8654 6.49972 14.0994 5.55454L14.2875 4.77061C14.3559 4.47443 14.3044 4.16324 14.1444 3.90483C13.9843 3.64641 13.7287 3.46169 13.4331 3.39089Z"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </g>
        </svg>
      </div>
      <p class="title-historic-data">{{ $t("team.pLoses.standing.title") }}</p>
      <p class="value-historic-data">{{ historicInfo.gamePenaltyLost ? historicInfo.gamePenaltyLost : 0 }}</p>
    </div>
    <!--    games lost icon-->
    <div class="box-historic-data games-lost">
      <div class="text-primary icon-game-lost">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0_3217_56597)">
            <path
              d="M16 7C16.3385 7.31779 16.7892 7.57518 17.3125 7.74944C17.8357 7.9237 18.4152 8.00946 19 7.99917"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M9 17C9 16.2044 9.31607 15.4413 9.87868 14.8787C10.4413 14.3161 11.2044 14 12 14C12.7956 14 13.5587 14.3161 14.1213 14.8787C14.6839 15.4413 15 16.2044 15 17"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M4.34974 1.79828L1.78232 4.36584C1.27836 4.87798 0.997181 5.56852 1.00002 6.28705C1.00286 7.00558 1.2895 7.69387 1.79749 8.20201C2.30548 8.71015 2.99367 8.99697 3.71216 8.99998C4.43064 9.00299 5.12121 8.72195 5.63344 8.21808L8.20267 5.64962C8.45554 5.39662 8.6561 5.09628 8.79291 4.76577C8.92971 4.43525 9.00008 4.08102 9 3.72331C8.99992 3.36559 8.92938 3.0114 8.79242 2.68095C8.65546 2.35049 8.45475 2.05025 8.20176 1.79737C7.94878 1.54449 7.64846 1.34392 7.31796 1.2071C6.98746 1.07029 6.63326 0.999916 6.27556 1C5.91787 1.00008 5.56369 1.07062 5.23326 1.20759C4.90283 1.34456 4.6026 1.54528 4.34974 1.79828Z"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M1.0816 10.5789C0.782795 12.8595 1.20569 15.1765 2.29092 17.2045C3.37615 19.2326 5.06922 20.8698 7.13247 21.8865C9.19573 22.9032 11.5256 23.2482 13.7949 22.8732C16.0643 22.4982 18.1592 21.4219 19.7857 19.7954C21.4121 18.169 22.4884 16.074 22.8634 13.8047C23.2385 11.5353 22.8934 9.20549 21.8768 7.14224C20.8601 5.07899 19.2228 3.38591 17.1948 2.30068C15.1667 1.21545 12.8498 0.792561 10.5691 1.09137"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M6.73592 11.2975C6.67238 11.2975 6.61144 11.2723 6.56651 11.2274C6.52158 11.1824 6.49634 11.1215 6.49634 11.0579C6.49634 10.9944 6.52158 10.9335 6.56651 10.8885C6.61144 10.8436 6.67238 10.8184 6.73592 10.8184"
              stroke="currentColor"
            />
            <path
              d="M6.73584 11.2975C6.79938 11.2975 6.86032 11.2723 6.90525 11.2274C6.95018 11.1824 6.97542 11.1215 6.97542 11.0579C6.97542 10.9944 6.95018 10.9335 6.90525 10.8885C6.86032 10.8436 6.79938 10.8184 6.73584 10.8184"
              stroke="currentColor"
            />
            <path
              d="M17.2774 11.2975C17.2139 11.2975 17.1529 11.2723 17.108 11.2274C17.0631 11.1824 17.0378 11.1215 17.0378 11.0579C17.0378 10.9944 17.0631 10.9335 17.108 10.8885C17.1529 10.8436 17.2139 10.8184 17.2774 10.8184"
              stroke="currentColor"
            />
            <path
              d="M17.2773 11.2975C17.3409 11.2975 17.4018 11.2723 17.4468 11.2274C17.4917 11.1824 17.5169 11.1215 17.5169 11.0579C17.5169 10.9944 17.4917 10.9335 17.4468 10.8885C17.4018 10.8436 17.3409 10.8184 17.2773 10.8184"
              stroke="currentColor"
            />
          </g>
          <defs>
            <clipPath id="clip0_3217_56597">
              <rect width="24" height="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </div>
      <p class="title-historic-data">{{ $t("team.loses.standing.title") }}</p>
      <p class="value-historic-data">{{ historicInfo.gameLost }}</p>
    </div>
    <!--    goals icon-->
    <div class="box-historic-data goals-pro">
      <div class="text-primary icon-goal-pro">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="soccer-goal-net">
            <path
              id="Vector"
              d="M23.0209 6.06698C23.0209 5.81281 22.9199 5.56905 22.7402 5.38933C22.5605 5.20961 22.3167 5.10864 22.0626 5.10864H1.93758C1.68342 5.10864 1.43966 5.20961 1.25994 5.38933C1.08022 5.56905 0.979248 5.81281 0.979248 6.06698V17.9321C0.979254 18.0955 1.02105 18.2562 1.10067 18.3989C1.18028 18.5417 1.29507 18.6617 1.43413 18.7475C1.57318 18.8334 1.73188 18.8822 1.89515 18.8895C2.05841 18.8967 2.22081 18.8621 2.36691 18.7889L4.81258 17.567H19.1876L21.6352 18.7898C21.7812 18.8628 21.9435 18.8973 22.1067 18.8899C22.2698 18.8826 22.4283 18.8337 22.5672 18.7478C22.7062 18.662 22.8208 18.5421 22.9004 18.3995C22.98 18.2569 23.0218 18.0964 23.0219 17.9331L23.0209 6.06698Z"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_2"
              d="M4.8125 8.94189H19.1875V17.5669H4.8125V8.94189Z"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_3"
              d="M19.1875 8.94195L22.74 5.3894"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_4"
              d="M4.81255 8.94195L1.26001 5.3894"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_5"
              d="M7.6875 8.94189V17.5669"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_6"
              d="M10.5625 8.94189V17.5669"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_7"
              d="M13.4375 8.94189V17.5669"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_8"
              d="M16.3125 8.94189V17.5669"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_9"
              d="M4.8125 11.8169H19.1875"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_10"
              d="M4.8125 14.6919H19.1875"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </g>
        </svg>
      </div>
      <p class="title-historic-data">{{ $t("team.goalsPro.standing.title") }}</p>
      <p class="value-historic-data">{{ historicInfo.goalPro }}</p>
    </div>
    <!--    goals against icon-->
    <div class="box-historic-data goals-against">
      <div class="text-primary icon-goal-against">
        <svg width="24" height="22" viewBox="0 0 24 22" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="Group">
            <path
              id="Vector"
              d="M15.4788 13.415C15.4788 13.415 10.5242 13.2137 8.35834 13.8462C7.99914 13.9493 7.67108 14.1398 7.40351 14.4007C7.13593 14.6616 6.93718 14.9847 6.825 15.3412C6.72265 15.6653 6.75294 16.0167 6.90922 16.3185C7.0655 16.6203 7.33503 16.8478 7.65875 16.9512L18.7754 20.4779L20.51 14.9962C20.0842 15.2588 19.607 15.4267 19.1106 15.4885C18.6142 15.5504 18.1104 15.5048 17.6332 15.3549C17.156 15.2049 16.7166 14.954 16.3449 14.6193C15.9732 14.2846 15.6778 13.8739 15.4788 13.415Z"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_2"
              d="M16.6477 20.8228L16.9352 19.9124"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_3"
              d="M13.907 19.9507L14.1945 19.0403"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_4"
              d="M8.42529 18.2161L8.71279 17.2961"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_5"
              d="M11.1663 19.0786L11.4538 18.1682"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_6"
              d="M15.4788 13.415L16.7342 5.55665C16.7646 5.42947 16.8437 5.31933 16.9546 5.24998C17.0168 5.22389 17.0836 5.21045 17.1511 5.21045C17.2185 5.21045 17.2853 5.22389 17.3475 5.24998C19.2642 5.82498 20.7688 6.15082 22.6471 6.72582C22.7083 6.74229 22.7654 6.77122 22.8149 6.8108C22.8643 6.85037 22.9051 6.89975 22.9346 6.95582C22.9821 7.07587 22.9821 7.20951 22.9346 7.32957L20.4621 15.0825"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_7"
              d="M16.3411 7.88525L22.2061 9.639"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <g id="Group_2">
              <path
                id="Vector_8"
                d="M1.00781 6.44783C1.00781 7.14001 1.14415 7.82541 1.40903 8.4649C1.67391 9.10438 2.06216 9.68544 2.5516 10.1749C3.04105 10.6643 3.6221 11.0526 4.26159 11.3175C4.90107 11.5823 5.58647 11.7187 6.27865 11.7187C6.97082 11.7187 7.65622 11.5823 8.29571 11.3175C8.93519 11.0526 9.51625 10.6643 10.0057 10.1749C10.4951 9.68544 10.8834 9.10438 11.1483 8.4649C11.4131 7.82541 11.5495 7.14001 11.5495 6.44783C11.5495 5.75566 11.4131 5.07026 11.1483 4.43077C10.8834 3.79129 10.4951 3.21024 10.0057 2.72079C9.51625 2.23135 8.93519 1.8431 8.29571 1.57822C7.65622 1.31334 6.97082 1.177 6.27865 1.177C5.58647 1.177 4.90107 1.31334 4.26159 1.57822C3.6221 1.8431 3.04105 2.23135 2.5516 2.72079C2.06216 3.21024 1.67391 3.79129 1.40903 4.43077C1.14415 5.07026 1.00781 5.75566 1.00781 6.44783Z"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </g>
          </g>
        </svg>
      </div>
      <p class="title-historic-data">{{ $t("team.goalsAgainst.standing.title") }}</p>
      <p class="value-historic-data">{{ historicInfo.goalAgainst }}</p>
    </div>
    <!--    goals dif icon-->
    <div class="box-historic-data goal-dif">
      <div class="text-primary icon-goal-difference">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="ranking-winner">
            <path
              id="Vector"
              d="M16.3126 13.9167V4.33333C16.3126 4.20748 16.2878 4.08287 16.2396 3.96659C16.1915 3.85032 16.1209 3.74468 16.0319 3.65569C15.9429 3.5667 15.8373 3.49611 15.721 3.44795C15.6047 3.39979 15.4801 3.375 15.3542 3.375H8.64591C8.39175 3.375 8.14799 3.47597 7.96827 3.65569C7.78855 3.83541 7.68758 4.07917 7.68758 4.33333V10.0833H1.93758C1.81173 10.0833 1.68711 10.1081 1.57084 10.1563C1.45457 10.2044 1.34893 10.275 1.25994 10.364C1.17095 10.453 1.10036 10.5587 1.0522 10.6749C1.00404 10.7912 0.979248 10.9158 0.979248 11.0417V19.6667C0.979248 19.9208 1.08022 20.1646 1.25994 20.3443C1.43966 20.524 1.68342 20.625 1.93758 20.625H22.0626C22.3167 20.625 22.5605 20.524 22.7402 20.3443C22.9199 20.1646 23.0209 19.9208 23.0209 19.6667V14.875C23.0209 14.6208 22.9199 14.3771 22.7402 14.1974C22.5605 14.0176 22.3167 13.9167 22.0626 13.9167H16.3126Z"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_2"
              d="M11.0417 6.25H11.5209C11.648 6.25 11.7699 6.30048 11.8597 6.39034C11.9496 6.48021 12.0001 6.60208 12.0001 6.72917V10.0833"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_3"
              d="M11.0417 10.0833H12.9584"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_4"
              d="M7.6875 10.0833V20.6249"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              id="Vector_5"
              d="M16.3125 13.9167V20.6251"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </g>
        </svg>
      </div>
      <p class="title-historic-data">{{ $t("team.goalDifference.standing.title") }}</p>
      <p class="value-historic-data">{{ historicInfo.goalDifference }}</p>
    </div>
    <!--    yellow cards icon-->
    <div class="box-historic-data yellow-cards">
      <div class="text-primary icon-yellow-cards">
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
          <path
            d="M4.50024 4.46251C4.50024 3.66477 5.14694 3.01807 5.94469 3.01807H11.7225C12.5202 3.01807 13.1669 3.66477 13.1669 4.46251V13.1292C13.1669 13.9269 12.5202 14.5736 11.7225 14.5736H5.94469C5.14694 14.5736 4.50024 13.9269 4.50024 13.1292V4.46251Z"
            fill="#FFCF00"
          />
        </svg>
      </div>
      <p class="title-historic-data">{{ $t("team.yellowCards.standing.title") }}</p>
      <p class="value-historic-data">{{ historicInfo.yellowCards ? historicInfo.yellowCards : 0 }}</p>
    </div>
    <!--    red cards icon-->
    <div class="box-historic-data red-cards">
      <div class="text-primary icon-red-cards">
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
          <path
            d="M4.00024 4.46251C4.00024 3.66477 4.64694 3.01807 5.44469 3.01807H11.2225C12.0202 3.01807 12.6669 3.66477 12.6669 4.46251V13.1292C12.6669 13.9269 12.0202 14.5736 11.2225 14.5736H5.44469C4.64694 14.5736 4.00024 13.9269 4.00024 13.1292V4.46251Z"
            fill="#E62D4F"
          />
        </svg>
      </div>
      <p class="title-historic-data">{{ $t("team.redCards.standing.title") }}</p>
      <p class="value-historic-data">{{ historicInfo.redCards ? historicInfo.redCards : 0 }}</p>
    </div>
  </div>
</template>

<style scoped>
.container-historic-stats {
  @apply grid grid-cols-2 gap-2 md:grid-cols-6 lg:flex lg:justify-between;
}

.box-historic-data {
  @apply bg-gray-900 rounded-lg py-4 px-2 flex flex-col justify-center items-center lg:basis-full;
}

.title-historic-data {
  @apply text-gray-500 uppercase text-[10px] mt-4 text-center leading-tight;
}

.value-historic-data {
  @apply text-white text-4xl font-bold leading-tight mt-4;
}
</style>
